import {
    type Theme,
    teamsDarkTheme,
    teamsHighContrastTheme,
    teamsLightTheme,
} from "@fluentui/react-components";

const THEME_STORAGE_KEY = "arvame.admin-tab.theme";

export function themeFromTeamsThemeString(themeString: string | undefined): Theme {
    switch (themeString) {
        case "dark":
            return teamsDarkTheme;
        case "contrast":
            return teamsHighContrastTheme;
        default:
            return teamsLightTheme;
    }
}

export function themeFromCachedTeamsThemeString(cached: string | null | undefined): Theme | undefined {
    const normalized = cached?.toLowerCase();
    if (normalized === "dark") {
        return teamsDarkTheme;
    }
    if (normalized === "contrast") {
        return teamsHighContrastTheme;
    }
    if (normalized === "default") {
        return teamsLightTheme;
    }
    return undefined;
}

/** Best-effort theme before TeamsFx finishes loading (URL hint, cache, system). */
export function readTeamsThemeHint(): Theme {
    if (typeof window === "undefined") {
        return teamsLightTheme;
    }

    const fromUrl = new URLSearchParams(window.location.search).get("theme")?.toLowerCase();
    if (fromUrl === "dark") {
        return teamsDarkTheme;
    }
    if (fromUrl === "contrast") {
        return teamsHighContrastTheme;
    }

    try {
        const cachedTheme = themeFromCachedTeamsThemeString(sessionStorage.getItem(THEME_STORAGE_KEY));
        if (cachedTheme) {
            return cachedTheme;
        }
    } catch {
        // sessionStorage may be unavailable
    }

    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
        return teamsDarkTheme;
    }

    return teamsLightTheme;
}

export function resolveFluentTheme(
    teamsTheme: Theme | undefined,
    themeString: string | undefined,
): Theme {
    if (teamsTheme) {
        return teamsTheme;
    }
    if (themeString) {
        return themeFromTeamsThemeString(themeString);
    }
    return readTeamsThemeHint();
}

export function persistTeamsThemeString(themeString: string | undefined): void {
    if (!themeString || typeof window === "undefined") {
        return;
    }
    try {
        sessionStorage.setItem(THEME_STORAGE_KEY, themeString);
    } catch {
        // ignore
    }
}

export function applyShellBackground(theme: Theme): void {
    const background = theme.colorNeutralBackground3;
    document.documentElement.style.backgroundColor = background;
    document.body.style.backgroundColor = background;
}

export function removeInitialLoader(): void {
    document.getElementById("initial-loader")?.remove();
}
